var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React, { useEffect, useMemo, useRef, useState } from 'react';
import ResizeObserver from 'resize-observer-polyfill';
import styled from 'styled-components';
import { Button, ButtonGroup, Collapse, FormHelperText, Typography } from '@material-ui/core';
import { Clear, Done } from '@material-ui/icons';
import { api, AppSnackbar, useTimeout } from '@sierralabs/core-ui';
import { Editor as EditorImport } from '@tinymce/tinymce-react';
import { useFormContext } from '../FormContext';
import { useDataSourceProps } from '../hooks/form/useDataSourceProps';
import { useHtmlImageHandler } from '../hooks/html/useHtmlImageHandler';
import { useImageReloader } from '../hooks/html/useImageReloader';
const StyledDiv = styled.div `
  .hidden {
    display: none;
  }
  iframe {
    max-width: 100% !important;
  }
  .html-container {
    min-height: 64px;
  }
`;
const StyledButtonGroup = styled(ButtonGroup) `
  border: 1px solid #ccc;
  background: #fff;
  border-top: 0;
  border-radius: 0;
`;
export const FormHtml = (_a) => {
    var _b, _c, _d, _e, _f;
    var { stateNode, element, edit, disabled, enableMedia } = _a, props = __rest(_a, ["stateNode", "element", "edit", "disabled", "enableMedia"]);
    const { formState, formValues, setFormState, notifyFormEdit, setFormValue } = useFormContext();
    const { processedHtml, processHtmlImageTags } = useHtmlImageHandler();
    const { error, reloadFailedImages } = useImageReloader();
    const editorElementRef = useRef();
    const contentElementRef = useRef(null);
    const iframeRef = useRef();
    // const setImageCallbackRef = useRef<Function>();
    const lastProcessedHtmlRef = useRef(processedHtml);
    const [buttonFocus, setButtonFocus] = useState(false);
    const [didInit, setDidInit] = useState(false);
    const [minHeight, setMinHeight] = useState(0);
    // const [fileDialogOpen, setFileDialogOpen] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [debounceSubmit, clearDebouncedSubmit] = useTimeout();
    const key = element.dataSource || element.key;
    const [value, setValue] = useState(((_b = element.config) === null || _b === void 0 ? void 0 : _b.html) || props.value || formValues[key] || '');
    const editable = (_c = element.config) === null || _c === void 0 ? void 0 : _c.editable;
    const [focus, setFocus] = useState((!editable && !value) || false);
    const showEditor = editable || (!disabled && edit && focus);
    const [touched, setTouched] = useState(false);
    const previousValueRef = useRef(value);
    const dataSourceProps = useDataSourceProps(element, value);
    const helperText = dataSourceProps.helperText || ((_d = element.config) === null || _d === void 0 ? void 0 : _d.helperText);
    // check to see if html content div size changes
    const observerRef = useRef(new ResizeObserver(() => {
        var _a;
        if (((_a = contentElementRef.current) === null || _a === void 0 ? void 0 : _a.clientHeight) > 0) {
            setMinHeight(contentElementRef.current.clientHeight);
        }
    }));
    useEffect(() => {
        const observer = observerRef.current;
        const element = contentElementRef.current;
        observer.observe(element);
        return () => {
            observer.unobserve(element);
        };
    }, []);
    const toolbar = useMemo(() => {
        const str = [
            'undo redo | formatselect | bold italic backcolor | alignleft aligncenter alignright alignjustify',
            `image media`,
            'link bullist numlist outdent indent | removeformat | help | code',
            'table tabledelete | tableprops tablerowprops tablecellprops | tableinsertrowbefore tableinsertrowafter tabledeleterow | tableinsertcolbefore tableinsertcolafter tabledeletecol'
        ];
        return str.join(' | ');
    }, []);
    const Editor = EditorImport;
    // refresh value if formValues change outside of the component when editable
    useEffect(() => {
        if (editable && previousValueRef.current !== formValues[key]) {
            setValue(formValues[key] || '');
            previousValueRef.current = formValues[key] || '';
        }
    }, [editable, formValues, key]);
    // after processing image tags in html save the results
    const { selectedFormElement } = formState;
    // for read only usage
    useEffect(() => {
        if (!editable &&
            processedHtml &&
            // prevent form from reselecting element on re-render while editing
            // another form element
            lastProcessedHtmlRef.current !== processedHtml &&
            selectedFormElement === element) {
            setValue(processedHtml);
            previousValueRef.current = processedHtml;
            if (!element.config)
                element.config = {};
            element.config.html = processedHtml;
            setFormState(formState => (Object.assign(Object.assign({}, formState), { selectedFormElement: element })));
            lastProcessedHtmlRef.current = processedHtml;
            notifyFormEdit();
            setTimeout(() => {
                if (iframeRef.current) {
                    reloadFailedImages(iframeRef.current);
                }
            }, 500);
        }
    }, [
        processedHtml,
        element,
        selectedFormElement,
        editable,
        notifyFormEdit,
        setFormState,
        reloadFailedImages
    ]);
    const handleCancel = () => {
        setValue(previousValueRef.current);
    };
    const handleSubmit = () => {
        if (!editable) {
            // for read only usage
            // look for data url image tags and upload to server
            processHtmlImageTags(value);
            // handle failed images for html container
            if (contentElementRef.current) {
                reloadFailedImages(contentElementRef.current);
            }
        }
        else {
            setFormValue(key, value);
        }
    };
    return (React.createElement(StyledDiv, { className: 'form-element-html', style: { minHeight } },
        !didInit && showEditor && (React.createElement(Typography, { variant: 'body2', color: 'textSecondary', style: { marginTop: 10 } }, "Loading editor...")),
        showEditor && (React.createElement(React.Fragment, null,
            React.createElement(Editor, { apiKey: api.environment.tinyApiKey, key: editable ? `${disabled}-${enableMedia}` : undefined, value: value || ((_e = element.config) === null || _e === void 0 ? void 0 : _e.defaultValue), init: {
                    menubar: false,
                    paste_data_images: true,
                    relative_urls: false,
                    remove_script_host: false,
                    placeholder: (_f = element.config) === null || _f === void 0 ? void 0 : _f.placeholder,
                    plugins: [
                        'advlist autolink lists link image charmap print preview anchor',
                        'searchreplace visualblocks code fullscreen',
                        'insertdatetime media table paste help wordcount',
                        'autoresize',
                        enableMedia ? 'media' : '',
                        'table'
                    ],
                    toolbar
                    // file_picker_callback: (setImageCallback: Function) => {
                    //   setImageCallbackRef.current = setImageCallback;
                    //   setFileDialogOpen(true);
                    // }
                }, disabled: editable && (edit || disabled), onFocus: () => {
                    setFocus(true);
                    if (!editable && edit) {
                        setFormState(state => (Object.assign(Object.assign({}, state), { selectedStateNode: stateNode, selectedFormElement: element })));
                    }
                }, onInit: (event) => {
                    var _a, _b;
                    editorElementRef.current = (_a = event.target) === null || _a === void 0 ? void 0 : _a.container;
                    const iframe = (_b = event.target) === null || _b === void 0 ? void 0 : _b.iframeElement;
                    iframeRef.current = iframe;
                    // applyImageStyling(iframe);
                    reloadFailedImages(iframe);
                    setDidInit(true);
                }, onBlur: () => {
                    setFocus(false);
                    if (!editable) {
                        handleSubmit();
                        return;
                    }
                    if (touched && formValues[key] !== value) {
                        debounceSubmit(() => handleSubmit(), 100);
                    }
                }, onEditorChange: (content, editor) => {
                    reloadFailedImages(editor.iframeElement);
                    setTouched(true);
                    setValue(content || '');
                }, onKeyDown: (event) => {
                    switch (event.key) {
                        case 'Escape':
                            setFocus(false);
                            return handleCancel();
                    }
                } }),
            React.createElement(Collapse, { in: didInit && (focus || buttonFocus) },
                React.createElement(StyledButtonGroup, { fullWidth: true, color: 'primary', variant: 'text' },
                    React.createElement(Button, { fullWidth: true, onFocus: () => setButtonFocus(true), onBlur: () => setButtonFocus(false), onClick: event => {
                            clearDebouncedSubmit();
                            event.stopPropagation();
                            setButtonFocus(false);
                            handleSubmit();
                        } },
                        React.createElement(Done, null)),
                    React.createElement(Button, { fullWidth: true, onFocus: () => setButtonFocus(true), onBlur: () => setButtonFocus(false), onClick: event => {
                            clearDebouncedSubmit();
                            event.stopPropagation();
                            setFocus(false);
                            setButtonFocus(false);
                            handleCancel();
                        } },
                        React.createElement(Clear, null)))),
            didInit && helperText && (React.createElement(FormHelperText, { error: dataSourceProps.error }, helperText)))),
        React.createElement("div", { ref: contentElementRef, className: `html-container${editable || (didInit && !disabled && edit && focus) ? ' hidden' : ''}`, dangerouslySetInnerHTML: { __html: value || '' }, onClick: () => {
                if (edit) {
                    setFocus(true);
                }
            } }),
        React.createElement(AppSnackbar, { severity: 'error', open: !!errorMessage, onClose: () => setErrorMessage('') }, errorMessage)));
};
export default FormHtml;
//# sourceMappingURL=FormHtml.js.map